import './WatchWristViewer.less';
import React, {useState, useEffect, useRef} from 'react';
import Images from '../../../models/image/Images';
import AspectContainer from '../../../components/aspect-container/AspectContainer';
import {projectionToVector} from '../../../utils/transform';
import {
    STRAP_CROSS_SECTION_SHAPE_CURVED,
    STRAP_CROSS_SECTION_SHAPE_CURVED_LARGE, STRAP_CROSS_SECTION_SHAPE_CURVED_SMALL,
    STRAP_CROSS_SECTION_SHAPE_RECTANGLE, STRAP_CROSS_SECTION_SHAPE_ROUNDED,
    STRAP_CROSS_SECTION_SHAPE_ROUNDED_LARGE,
    STRAP_CROSS_SECTION_SHAPE_ROUNDED_SMALL,
} from '../../../model-constants/wrist-prototype/strapCrossSectionShapes';


export default function WatchWristViewer({watchPrototype, wristPrototype, images, zoomScale = 1}) {
    const watchImage = images.find(t => watchPrototype.layer_image_id === t.id);
    const wristImage = images.find(t => wristPrototype.layer_image_id === t.id);

    const RENDER_AREA_WIDTH = 300;

    const frameRef = useRef(null);

    const calcMmPixels = () => {
        const frame = frameRef.current;

        if (frame === null) {
            return 1;
        }

        return frame.clientWidth / RENDER_AREA_WIDTH;
    };

    const [mmPixels, setMmPixels] = useState(calcMmPixels());

    const refreshMmPixels = () => {
        setMmPixels(calcMmPixels());
    };

    setTimeout(() => {
        refreshMmPixels();
    });

    useEffect(() => {
        refreshMmPixels();
    }, [frameRef]);

    useEffect(() => {
        const listener = () => refreshMmPixels();

        window.addEventListener('resize', listener);

        return () => window.removeEventListener('resize', listener);
    }, []);

    const mm2px = (mm) => {
        return mm * mmPixels;
    };

    const {
        x: wrist_translate_x,
        y: wrist_translate_y
    } = projectionToVector(
        wristPrototype.layer_position_x,
        wristPrototype.layer_position_y,
        wristPrototype.layer_angle * Math.PI / 180
    );

    const {
        x: watch_translate_x,
        y: watch_translate_y
    } = projectionToVector(
        watchPrototype.layer_position_x,
        watchPrototype.layer_position_y,
        watchPrototype.layer_angle * Math.PI / 180
    );

    const rectangle = watchPrototype.strap_cross_section_shape === STRAP_CROSS_SECTION_SHAPE_RECTANGLE;
    const rounded = watchPrototype.strap_cross_section_shape === STRAP_CROSS_SECTION_SHAPE_ROUNDED;
    const roundedSmall = watchPrototype.strap_cross_section_shape === STRAP_CROSS_SECTION_SHAPE_ROUNDED_SMALL;
    const roundedLarge = watchPrototype.strap_cross_section_shape === STRAP_CROSS_SECTION_SHAPE_ROUNDED_LARGE;
    const curved = watchPrototype.strap_cross_section_shape === STRAP_CROSS_SECTION_SHAPE_CURVED;
    const curvedSmall = watchPrototype.strap_cross_section_shape === STRAP_CROSS_SECTION_SHAPE_CURVED_SMALL;
    const curvedLarge = watchPrototype.strap_cross_section_shape === STRAP_CROSS_SECTION_SHAPE_CURVED_LARGE;

    const borderRadiusPadding = mm2px(watchPrototype.lug_width) / (() => {
        if (curved) {
            return 10;
        }

        if (curvedSmall) {
            return 14;
        }

        if (curvedLarge) {
            return 6;
        }

        if (rounded) {
            return 8;
        }

        if (roundedSmall) {
            return 12;
        }

        if (roundedLarge) {
            return 6;
        }

        return 100000000000;
    })();

    const borderRadius = (() => {
        if (curved) {
            return `45%/${borderRadiusPadding}px`;
        }

        if (curvedSmall) {
            return `45%/${borderRadiusPadding}px`;
        }

        if (curvedLarge) {
            return `45%/${borderRadiusPadding}px`;
        }

        if (rounded) {
            return borderRadiusPadding;
        }

        if (roundedSmall) {
            return borderRadiusPadding;
        }

        if (roundedLarge) {
            return borderRadiusPadding;
        }

        return '0px';
    })();

    return <div className="watch-wrist-viewer">
        <AspectContainer ratio={1}>
            <div className="preview" ref={frameRef}>
                <div
                    className="coordinates"
                    style={{
                        transformOrigin: '50% 50%',
                        transform: [
                            `translateX(${mm2px(wristPrototype.coords_position_x)}px)`,
                            `translateY(${mm2px(wristPrototype.coords_position_y)}px)`,
                            `scale(${zoomScale})`,
                            `rotate(${wristPrototype.coords_angle}deg)`,
                        ].join(' ')
                    }}
                >
                    <img
                        key={wristImage.id}
                        src={Images.getUrl(wristImage)}
                        width={wristImage.width + 'px'}
                        height={wristImage.height + 'px'}
                        style={{
                            width: "100%",
                            height: "auto",
                            transformOrigin: '50% 50%',
                            transform: [
                                `scale(${zoomScale})`,
                                `scale(${(wristPrototype.layer_width / RENDER_AREA_WIDTH)})`,
                                `scaleX(${wristPrototype.layer_scale_x / 100})`,
                                `scaleY(${wristPrototype.layer_scale_y / 100})`,
                                `rotate(${wristPrototype.layer_angle}deg)`,
                                `translateX(${mm2px(wrist_translate_x) / (wristPrototype.layer_width / RENDER_AREA_WIDTH)}px)`,
                                `translateY(${mm2px(wrist_translate_y) / (wristPrototype.layer_width / RENDER_AREA_WIDTH)}px)`,
                            ].join(' ')
                        }}
                        onLoad={() => refreshMmPixels()}
                    />

                    {
                        new Array(10).fill(null).map((_, index, arr) => {
                            const isFirst = index === 0;
                            const isLast = index === arr.length - 1;
                            const isBeforeLast = index === arr.length - 2;

                            // overflow height
                            const calcSubPadding = K => (K / arr.length) * (arr.length - index) * mm2px(watchPrototype.lug_width);
                            const subPaddingLarge = calcSubPadding(0.10);
                            const subPaddingSmall = calcSubPadding(0.04);
                            const subPadding = wristPrototype.wrist_width - watchPrototype.lugs_distance >= subPaddingSmall
                                ? subPaddingLarge : subPaddingSmall;

                            const baseHeight = mm2px(Math.max(watchPrototype.lugs_distance, wristPrototype.wrist_width));
                            const paddedHeight = baseHeight + borderRadiusPadding + subPadding;

                            // css filter
                            const opacity = Math.min(1, 0.5 + 0.05 * (10 / arr.length) * (index + 2));
                            const dropShadow = `drop-shadow(0 0 ${mm2px(watchPrototype.lug_width / 10 * zoomScale)}px rgba(0,0,0,0.5))`;

                            return <div
                                key={watchImage.id + ';' + index}
                                className="img-overflow"
                                style={{
                                    width: isLast ? watchImage.width : (isFirst ? mm2px(watchPrototype.case_diameter) : mm2px(watchPrototype.lug_width)),
                                    height: (isLast || isFirst) ? baseHeight : paddedHeight,
                                    borderRadius: !(isLast || isFirst) ? borderRadius : '',
                                    overflow: 'hidden',
                                    transformOrigin: '50% 50%',
                                    transform: `scale(${zoomScale})`,
                                    filter: [
                                        `brightness(${opacity})`,
                                        `opacity(${opacity})`,
                                        isFirst ? dropShadow : ''
                                    ].join(' '),
                                }}
                            >
                                <img
                                    key={watchImage.id}
                                    src={Images.getUrl(watchImage)}
                                    width={watchImage.width + 'px'}
                                    height={watchImage.height + 'px'}
                                    style={{
                                        width: mm2px(RENDER_AREA_WIDTH),
                                        height: 'auto',
                                        transformOrigin: '50% 50%',
                                        transform: [
                                            `scale(${(watchPrototype.layer_width / RENDER_AREA_WIDTH)})`,
                                            `scaleX(${watchPrototype.layer_scale_x / 100})`,
                                            `scaleY(${watchPrototype.layer_scale_y / 100})`,
                                            `rotate(${watchPrototype.layer_angle}deg)`,
                                            `translateX(${mm2px(watch_translate_x) / (watchPrototype.layer_width / RENDER_AREA_WIDTH)}px)`,
                                            `translateY(${mm2px(watch_translate_y) / (watchPrototype.layer_width / RENDER_AREA_WIDTH)}px)`,
                                        ].join(' '),
                                    }}
                                    onLoad={() => refreshMmPixels()}
                                />
                            </div>;
                        })
                    }

                </div>

            </div>
        </AspectContainer>
    </div>;
}
